import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { BaseSearchCriteria } from '../../../models/searchCriteria/BaseSearchCriteria';
import { TooltipDirective } from '../../../utils/tooltip.directive';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { ThOrderingComponent } from '../../shared/th-ordering/th-ordering.component';
import { FilterPanelComponent } from '../../shared/filter-panel/filter-panel.component';
import { AlertService } from '../../shared/alert/alert.service';
import { StorageService } from '../../../services/storage.service';
import { Locality } from '../../../models/Locality';
import { LocalityService } from '../../../services/locality.service';
import { LOCALITY_SEARCH_CRITERIA_KEY, LocalitySearchCriteria } from '../../../models/searchCriteria/LocalitySearchCriteria';

@Component({
  selector: 'app-localities',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    ThOrderingComponent,
    ConfirmAlertComponent,
    HasPermissionDirective,
    FilterPanelComponent,
    FormsModule,
    ReactiveFormsModule,
    TooltipDirective,
  ],
  templateUrl: './localities.component.html',
  styleUrl: './localities.component.scss',
})
export class LocalitiesComponent implements OnInit {

  searchForm!: FormGroup;
  emptySearchCriteria: LocalitySearchCriteria = { cityName: undefined, pageSize: AppConstants.pageSize, pageNumber: 1 };
  searchCriteria: LocalitySearchCriteria = { ...this.emptySearchCriteria };

  localities: Locality[] = [];

  selectedLocality?: Locality;
  totalItems: number = 0;

  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';
  confirmOperationType: 'DEL' | undefined = undefined;

  constructor(
    private localityService: LocalityService,
    private alertService: AlertService,
    private storageService: StorageService,
    private fullSpinnerService: FullSpinnerService,
    private fb: FormBuilder,
    private router: Router) {
    this.searchForm = this.fb.group({
      cityName: [undefined],
    });
    this.clearFilter();
    fullSpinnerService.spinner(undefined);
  }

  ngOnInit(): void {
    this.fullSpinnerService.spinner(undefined);
    if (AppConstants.storeNavigationInfo) {
      let scObj = this.storageService.retrieveNavigationInfo(LOCALITY_SEARCH_CRITERIA_KEY);
      if (scObj !== null) {
        this.searchCriteria = scObj;
      } else {
        this.storageService.saveNavigationInfo(this.searchCriteria, LOCALITY_SEARCH_CRITERIA_KEY);
      }
    }
    this.searchForm.patchValue(this.searchCriteria);
    this.find();
  }

  edit(id?: number) {
    this.router.navigate(['localities', id]);
  }

  find() {
    this.fullSpinnerService.spinner('');
    this.storageService.saveNavigationInfo(this.searchCriteria, LOCALITY_SEARCH_CRITERIA_KEY);
    this.localityService.find(this.searchCriteria).subscribe({
      next: (res) => {
        this.localities = res.items;
        this.totalItems = res.totalItems;
      }, complete: () => {
        this.fullSpinnerService.spinner(undefined);
      }, error: () => {
        this.fullSpinnerService.spinner(undefined);
      }
    });
  }

  search(): void {
    this.searchCriteria = {
      ...this.searchCriteria,
      ...this.searchForm.value
    };
    this.find();
  }

  clearFilter(withSearch: boolean = false) {
    this.searchCriteria = { ...this.emptySearchCriteria };
    this.searchForm.patchValue(this.searchCriteria);
    if (withSearch)
      this.find();
  }

  pageChanged(event: number) {
    this.searchCriteria.pageNumber = event;
    this.find();
  }

  ordering(baseSearchCriteria: BaseSearchCriteria | undefined) {
    this.searchCriteria = baseSearchCriteria as LocalitySearchCriteria;
    this.find();
  }

  confirmOperation(locality: Locality, confirmOperationType: 'DEL' | undefined) {
    this.showConfirmationModal = true;
    this.confirmOperationType = confirmOperationType;
    if (confirmOperationType === 'DEL') {
      this.confirmationMessage = 'Eliminazione località - Sicuro di voler eliminare ' + locality.cityName + ' ?';
    }
    this.selectedLocality = locality;
  }

  cancelModalOperationExec() {
    this.selectedLocality = undefined;
    this.showConfirmationModal = false;
  }

  modalOperationExec() {
    this.fullSpinnerService.spinner('');
    if (this.confirmOperationType === 'DEL') {
      this.localityService.delete(this.selectedLocality!.id!).subscribe({
        next: () => {
          this.alertService.addErrors(AppConstants.titleDeleteSuccessMessage, [AppConstants.successMessage], true);
          this.cancelModalOperationExec();
          this.fullSpinnerService.spinner(undefined);
          this.find();
        }, error: () => {
          this.cancelModalOperationExec();
          this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
          this.fullSpinnerService.spinner(undefined);
        }
      });
    }
  }
}
